// Player-facing analytics tracking. Mounted ONLY inside the customer portal
// (never on /office admin pages). Sends pageviews, clicks and conversions to
// the backend, deduped under a rolling 30-min session id.
import axios from 'axios';
import { API } from './apiConfig';

const SID_KEY = 'maks_an_sid';
const SID_TS_KEY = 'maks_an_ts';
const UTM_KEY = 'maks_an_utm';
const NEW_KEY = 'maks_an_new';
const SESSION_TTL = 30 * 60 * 1000; // 30 minutes idle

let cachedEnabled = null;
let lastClickTs = 0;

const uuid = () =>
  (window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

function getSessionId() {
  const now = Date.now();
  let sid = localStorage.getItem(SID_KEY);
  const ts = parseInt(localStorage.getItem(SID_TS_KEY) || '0', 10);
  if (!sid || now - ts > SESSION_TTL) {
    sid = uuid();
    localStorage.setItem(SID_KEY, sid);
    localStorage.setItem(NEW_KEY, '1');
  }
  localStorage.setItem(SID_TS_KEY, String(now));
  return sid;
}

function detectDevice() {
  const ua = navigator.userAgent || '';
  let device_type = 'desktop';
  if (/ipad|tablet/i.test(ua)) device_type = 'tablet';
  else if (/mobi|android|iphone/i.test(ua)) device_type = 'mobile';
  let os = 'Unknown';
  if (/windows/i.test(ua)) os = 'Windows';
  else if (/android/i.test(ua)) os = 'Android';
  else if (/iphone|ipad|ios/i.test(ua)) os = 'iOS';
  else if (/mac/i.test(ua)) os = 'macOS';
  else if (/linux/i.test(ua)) os = 'Linux';
  let browser = 'Other';
  if (/edg/i.test(ua)) browser = 'Edge';
  else if (/chrome|crios/i.test(ua)) browser = 'Chrome';
  else if (/firefox/i.test(ua)) browser = 'Firefox';
  else if (/safari/i.test(ua)) browser = 'Safari';
  return { device_type, os, browser, screen: `${window.screen.width}x${window.screen.height}` };
}

function parseUTM() {
  const p = new URLSearchParams(window.location.search);
  const utm = {
    utm_source: p.get('utm_source') || '',
    utm_medium: p.get('utm_medium') || '',
    utm_campaign: p.get('utm_campaign') || '',
    utm_term: p.get('utm_term') || '',
    utm_content: p.get('utm_content') || '',
  };
  if (Object.values(utm).some(Boolean)) {
    localStorage.setItem(UTM_KEY, JSON.stringify(utm));
    return utm;
  }
  const stored = localStorage.getItem(UTM_KEY);
  if (stored) {
    try { return JSON.parse(stored); } catch { /* noop */ }
  }
  return utm;
}

function deriveSource(utm, referrer) {
  if (utm.utm_source) return utm.utm_source;
  if (!referrer) return 'Direct';
  try {
    const h = new URL(referrer).hostname.replace(/^www\./, '');
    if (h.includes(window.location.hostname)) return 'Direct';
    if (h.includes('google')) return 'Google';
    if (h.includes('facebook') || h.includes('fb.')) return 'Facebook';
    if (h.includes('instagram')) return 'Instagram';
    if (h.includes('whatsapp') || h.includes('wa.me')) return 'WhatsApp';
    if (h.includes('t.co') || h.includes('twitter') || h.includes('x.com')) return 'Twitter/X';
    return h;
  } catch {
    return 'Direct';
  }
}

function cAuthHeader() {
  const t = localStorage.getItem('customer_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function send(payload) {
  try {
    await axios.post(`${API}/analytics/track`, payload, { headers: cAuthHeader() });
  } catch {
    /* silent — analytics must never break the UX */
  }
}

export async function isAnalyticsEnabled() {
  if (cachedEnabled !== null) return cachedEnabled;
  try {
    const res = await axios.get(`${API}/analytics/config`);
    cachedEnabled = !!res.data.enabled;
  } catch {
    cachedEnabled = false;
  }
  return cachedEnabled;
}

export async function trackPageView(path) {
  if (!(await isAnalyticsEnabled())) return;
  const sid = getSessionId();
  const isNew = localStorage.getItem(NEW_KEY) === '1';
  const utm = parseUTM();
  const referrer = document.referrer || '';
  send({
    session_id: sid,
    event_type: 'pageview',
    path,
    referrer,
    ...utm,
    source: deriveSource(utm, referrer),
    landing: isNew,
    ...detectDevice(),
    ts: new Date().toISOString(),
  });
  if (isNew) localStorage.removeItem(NEW_KEY);
}

export async function trackClick(label) {
  if (!(await isAnalyticsEnabled())) return;
  const now = Date.now();
  if (now - lastClickTs < 400) return; // throttle rapid clicks
  lastClickTs = now;
  send({
    session_id: getSessionId(),
    event_type: 'click',
    path: window.location.pathname,
    label: (label || '').slice(0, 60),
    ts: new Date().toISOString(),
  });
}

export async function trackConversion(conversion_type, value = 0) {
  if (!(await isAnalyticsEnabled())) return;
  send({
    session_id: getSessionId(),
    event_type: 'conversion',
    conversion_type,
    value: Number(value) || 0,
    path: window.location.pathname,
    ts: new Date().toISOString(),
  });
}
